// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { isSafeNSName, useSafeNSEnabled } from '@mysten/core';
import { useSafeClientQuery, useSafeClient } from '@safecoin/dapp-kit';
import { type SafeJsonRpcClient, type SafeSystemStateSummary } from '@safecoin/safe/jsonRpc';
import {
	isValidTransactionDigest,
	isValidSafeAddress,
	isValidSafeObjectId,
	normalizeSafeObjectId,
} from '@safecoin/safe/utils';
import { useQuery } from '@tanstack/react-query';

const isGenesisLibAddress = (value: string): boolean => /^(0x|0X)0{0,39}[12]$/.test(value);

type Results = { id: string; label: string; type: string }[];

const getResultsForTransaction = async (client: SafeJsonRpcClient, query: string) => {
	if (!isValidTransactionDigest(query)) return null;
	const txdata = await client.getTransactionBlock({ digest: query });
	return [
		{
			id: txdata.digest,
			label: txdata.digest,
			type: 'transaction',
		},
	];
};

const getResultsForObject = async (client: SafeJsonRpcClient, query: string) => {
	const normalized = normalizeSafeObjectId(query);
	if (!isValidSafeObjectId(normalized)) return null;

	const { data, error } = await client.getObject({ id: normalized });
	if (!data || error) return null;

	return [
		{
			id: data.objectId,
			label: data.objectId,
			type: 'object',
		},
	];
};

const getResultsForCheckpoint = async (client: SafeJsonRpcClient, query: string) => {
	// Checkpoint digests have the same format as transaction digests:
	if (!isValidTransactionDigest(query)) return null;

	const { digest } = await client.getCheckpoint({ id: query });
	if (!digest) return null;

	return [
		{
			id: digest,
			label: digest,
			type: 'checkpoint',
		},
	];
};

const getResultsForAddress = async (client: SafeJsonRpcClient, query: string, safeNSEnabled: boolean) => {
	if (safeNSEnabled && isSafeNSName(query)) {
		const resolved = await client.resolveNameServiceAddress({ name: query.toLowerCase() });
		if (!resolved) return null;
		return [
			{
				id: resolved,
				label: resolved,
				type: 'address',
			},
		];
	}

	const normalized = normalizeSafeObjectId(query);
	if (!isValidSafeAddress(normalized) || isGenesisLibAddress(normalized)) return null;

	const [from, to] = await Promise.all([
		client.queryTransactionBlocks({
			filter: { FromAddress: normalized },
			limit: 1,
		}),
		client.queryTransactionBlocks({
			filter: { ToAddress: normalized },
			limit: 1,
		}),
	]);

	if (!from.data?.length && !to.data?.length) return null;

	return [
		{
			id: normalized,
			label: normalized,
			type: 'address',
		},
	];
};

// Query for validator by pool id or safe address.
const getResultsForValidatorByPoolIdOrSafeAddress = async (
	systemStateSummery: SafeSystemStateSummary | null,
	query: string,
) => {
	const normalized = normalizeSafeObjectId(query);
	if ((!isValidSafeAddress(normalized) && !isValidSafeObjectId(normalized)) || !systemStateSummery)
		return null;

	// find validator by pool id or safe address
	const validator = systemStateSummery.activeValidators?.find(
		({ stakingPoolId, safeAddress }) => stakingPoolId === normalized || safeAddress === query,
	);

	if (!validator) return null;

	return [
		{
			id: validator.safeAddress || validator.stakingPoolId,
			label: normalized,
			type: 'validator',
		},
	];
};

export function useSearch(query: string) {
	const client = useSafeClient();
	const { data: systemStateSummery } = useSafeClientQuery('getLatestSafeSystemState');
	const safeNSEnabled = useSafeNSEnabled();

	return useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ['search', query],
		queryFn: async () => {
			const results = (
				await Promise.allSettled([
					getResultsForTransaction(client, query),
					getResultsForCheckpoint(client, query),
					getResultsForAddress(client, query, safeNSEnabled),
					getResultsForObject(client, query),
					getResultsForValidatorByPoolIdOrSafeAddress(systemStateSummery || null, query),
				])
			).filter((r) => r.status === 'fulfilled' && r.value) as PromiseFulfilledResult<Results>[];

			return results.map(({ value }) => value).flat();
		},
		enabled: !!query,
		gcTime: 10000,
	});
}
